/**
 * Real-time event stream client.
 *
 * Opens an EventSource to /api/dashboard/stream?token=<jwt>. On any meaningful
 * event the caller's onEvent handler runs. Auto-reconnects with backoff if the
 * connection drops.
 *
 * Token rotation: when JWT is close to expiry, caller is responsible for
 * passing a fresh one via reconnect().
 */

export interface RealtimeEvent {
  type: 'hello' | 'opportunity_changed' | 'call_ingested' | 'email_message' | 'whatsapp_message' | 'sequence_updated' | 'snapshot_refresh_hint';
  clientId?: string;
  ghlOpportunityId?: string;
  callId?: string;
  ghlCallId?: string;
  conversationId?: string;
  sequenceName?: string;
  ts?: number;
}

export interface RealtimeOptions {
  getToken: () => Promise<string>;
  onEvent: (event: RealtimeEvent) => void;
  onStatusChange?: (status: 'connecting' | 'open' | 'closed') => void;
}

export class RealtimeClient {
  private es: EventSource | null = null;
  private opts: RealtimeOptions;
  private reconnectMs = 1500;
  private maxReconnectMs = 30_000;
  private closedByCaller = false;

  constructor(opts: RealtimeOptions) {
    this.opts = opts;
  }

  async connect(): Promise<void> {
    this.closedByCaller = false;
    await this.openConnection();
  }

  close(): void {
    this.closedByCaller = true;
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    this.opts.onStatusChange?.('closed');
  }

  private async openConnection(): Promise<void> {
    if (this.closedByCaller) return;
    this.opts.onStatusChange?.('connecting');
    let token: string;
    try {
      token = await this.opts.getToken();
    } catch {
      this.scheduleReconnect();
      return;
    }
    // Closed while waiting for the ticket (e.g. the session changed): opening now
    // would leave a stream nobody closes, refreshing with the previous session.
    if (this.closedByCaller) return;
    const url = `/api/dashboard/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    this.es = es;

    const eventTypes: RealtimeEvent['type'][] = [
      'hello', 'opportunity_changed', 'call_ingested', 'email_message',
      'whatsapp_message', 'sequence_updated', 'snapshot_refresh_hint',
    ];
    for (const evtType of eventTypes) {
      es.addEventListener(evtType, (ev) => {
        try {
          const data = JSON.parse((ev as MessageEvent).data) as RealtimeEvent;
          this.opts.onEvent({ ...data, type: evtType });
        } catch {
          // ignore malformed events
        }
      });
    }

    es.onopen = () => {
      this.reconnectMs = 1500; // reset backoff on successful connect
      this.opts.onStatusChange?.('open');
    };

    es.onerror = () => {
      es.close();
      this.es = null;
      if (!this.closedByCaller) this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.closedByCaller) return;
    this.opts.onStatusChange?.('connecting');
    const delay = this.reconnectMs;
    this.reconnectMs = Math.min(this.maxReconnectMs, this.reconnectMs * 2);
    setTimeout(() => void this.openConnection(), delay);
  }
}
